import toast from 'react-hot-toast';

import { api } from '@/utils/api';

export const useUpdateTeamData = () => {
  const utils = api.useUtils();

  return api.user.updateTeamData.useMutation({
    onSuccess: async (res) => {
      await Promise.all([
        utils.user.me.invalidate(),
        utils.team.teamUser.invalidate({ teamId: res.teamId, userId: res.userId }),
        utils.team.teamUserWithAll.invalidate({ teamId: res.teamId, userId: res.userId }),
        utils.superAdmin.doneForYouUsers.invalidate(),
      ]);
    },
  });
};

export const useUnipileAuthMutation = () => {
  const utils = api.useUtils();

  return api.user.unipileGenAuthUrl.useMutation({
    onSuccess: () => {
      // Poll for updates to detect when the auth completes in the popup
      const interval = setInterval(() => {
        void utils.user.me.invalidate();
        void utils.team.teamUser.invalidate();
        void utils.aiStep.fetchNext.invalidate();
      }, 2000);

      // Stop polling after 2 minutes
      setTimeout(() => {
        clearInterval(interval);
      }, 120000);
    },
    onError: (error) => {
      // Show user-friendly toast for Unipile errors (e.g., API token invalid, service unavailable)
      // The backend already classifies errors and returns appropriate messages
      toast.error(error.message);
    },
  });
};

export const useUnipileCopyFromTeamMutation = () => {
  const utils = api.useUtils();

  return api.user.unipileCopyFromTeam.useMutation({
    onSuccess: async () => {
      await Promise.all([utils.user.me.invalidate(), utils.team.teamUser.invalidate()]);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useUnipileDisconnectMutation = () => {
  const utils = api.useUtils();

  return api.user.unipileDisconnect.useMutation({
    onSuccess: () => {
      void utils.user.me.invalidate();
      void utils.team.teamUser.invalidate();
    },
    onError: (error) => {
      // Show user-friendly toast for Unipile disconnect errors
      toast.error(error.message);
    },
  });
};

export const useUpdateTeamThrottlingData = () => {
  const utils = api.useUtils();

  return api.user.updateUserTeamThrottlingData.useMutation({
    onSuccess: async (res) => {
      await Promise.all([
        utils.user.me.invalidate(),
        utils.team.teamUser.invalidate({ teamId: res.teamId, userId: res.userId }),
        utils.team.teamUserWithAll.invalidate({ teamId: res.teamId, userId: res.userId }),
      ]);
    },
  });
};

export const useUpdateProfilePicture = () => {
  const utils = api.useUtils();

  return api.user.updateProfilePicture.useMutation({
    onSuccess: (res) => {
      const teamId = res.teamId;
      const userId = res.userId;

      utils.user.me.setData(undefined, (data) => {
        if (data && data.userTeam && data.userTeam.teamId == res.teamId && data.userTeam.userId == res.userId) {
          return {
            ...data,
            userTeam: {
              ...data.userTeam,
              profilePicture: res.profilePicture,
            },
          };
        }
        return data;
      });
      utils.team.teamUser.setData({ teamId, userId }, (data) => {
        if (data && data.teamId == res.teamId && data.userId == res.userId) {
          return {
            ...data,
            profilePicture: res.profilePicture,
          };
        }
        return data;
      });
    },
  });
};

export const useMeUpdateMutation = () => {
  const utils = api.useUtils();

  return api.user.meUpdate.useMutation({
    onSuccess: async () => {
      await utils.user.me.invalidate();
    },
  });
};

export const useRecordSignUpDataMutation = () => {
  const utils = api.useUtils();

  return api.user.recordSignUpData.useMutation({
    onSuccess: async () => {
      await utils.user.me.invalidate();
    },
  });
};

export const useSuperadminMeDeleteFromTeamMutation = () => {
  const utils = api.useUtils();

  return api.superAdmin.deleteMeFromTeam.useMutation({
    onSuccess: async (_, teamId) => {
      await utils.superAdmin.teamId.invalidate(teamId);
    },
  });
};

export const useSuperadminMeAddToTeamMutation = () => {
  const utils = api.useUtils();

  return api.superAdmin.addMeToTeam.useMutation({
    onSuccess: async (_, teamId) => {
      await utils.superAdmin.teamId.invalidate(teamId);
    },
  });
};

export const useUpdateEmailCredentials = () => {
  const utils = api.useUtils();

  return api.team.updateEmailCredential.useMutation({
    onSuccess: async (_, { teamId }) => {
      await utils.team.emailCredentials.invalidate({ teamId });
    },
  });
};

export const useTestEmailCredentials = () => {
  const utils = api.useUtils();

  return api.team.testEmailCredential.useMutation({
    onSuccess: async () => {
      await utils.team.emailCredentials.invalidate();
    },
  });
};

export const useDeleteEmailCredentials = () => {
  const utils = api.useUtils();

  return api.team.deleteEmailCredential.useMutation({
    onSuccess: async (_, { teamId }) => {
      await utils.team.emailCredentials.invalidate({ teamId });
    },
  });
};

export const useDisconnectLinkedin = () => {
  const utils = api.useUtils();

  return api.user.disconnectLinkedin.useMutation({
    onSuccess: async () => {
      await Promise.all([utils.team.teamUser.invalidate(), utils.user.me.invalidate()]);
    },
  });
};

export const useUpsertRunCalendarBulk = () => {
  const utils = api.useUtils();

  return api.user.upsertCalendarBulk.useMutation({
    onSuccess: () => {
      void utils.team.teamUser.invalidate();
      void utils.user.me.invalidate();
      void utils.user.getCalendar.invalidate();
    },
  });
};

export const useUpdateRunCalendar = () => {
  const utils = api.useUtils();

  return api.user.updateCalendar.useMutation({
    onSuccess: () => {
      void utils.user.getCalendar.invalidate();
    },
  });
};

export const useCreateAudioDropFile = () => {
  const utils = api.useUtils();

  return api.user.createAudioDropFile.useMutation({
    onSuccess: () => {
      void utils.user.getAudioDropFiles.invalidate();
    },
  });
};

export const useDeleteAudioDropFile = () => {
  const utils = api.useUtils();

  return api.user.deleteAudioDropFile.useMutation({
    onSuccess: () => {
      void utils.user.getAudioDropFiles.invalidate();
    },
  });
};
